import { useQuery } from '@apollo/client';
import { TrackEventInput } from '@updater/ui-tracker';
import { useEffect } from 'react';
import { useTracking } from 'react-tracking';
import {
  AddressInput,
  QueryDollyMoveArgs,
  Query,
} from '@updater/consumer-graph';
import { cleanAddress } from 'utils/addresses';
import { GetDollyEligibility } from 'utils/queries';
import { useGetUserInfoQuery } from './use-get-user-info-query';

export const useDollyEligibility = () => {
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'mys',
    object: 'dolly_eligibility',
    verb: 'tracked',
  });

  const {
    data: userData,
    error: userError,
    loading: userLoading,
  } = useGetUserInfoQuery();
  const currentMove = userData?.getUser?.user?.currentMove;
  // `cleanAddress` returns address fields typed as nullable, but `AddressInput`
  // requires non-null strings. We cast here instead of widening the upstream
  // helper. Safe because the query is skipped until `currentMove` exists and
  // downstream consumers treat missing address parts as empty strings at runtime.
  const result = useQuery<Query, QueryDollyMoveArgs>(GetDollyEligibility, {
    variables: {
      input: {
        moveId: currentMove?.id ?? '',
        fromAddress: (currentMove?.fromAddress
          ? cleanAddress(currentMove.fromAddress)
          : {
              street: '',
              city: '',
              state: '',
              postalCode: '',
            }) as AddressInput,
        toAddress: (currentMove?.toAddress
          ? cleanAddress(currentMove.toAddress)
          : {
              street: '',
              city: '',
              state: '',
              postalCode: '',
            }) as AddressInput,
        moveDate: currentMove?.date ?? null,
      },
    },
    skip: userLoading || !userData || !!userError || !currentMove,
  });

  useEffect(() => {
    if (result.data) {
      trackEvent({
        details: {
          eligible: result.data.dollyMove?.eligible,
        },
      });
    }
  }, [trackEvent, result]);

  return result;
};
